import React, { useState, useMemo, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { useContent } from "../context/ContentContext";
import { fetchProducts } from "../services/api";
import ProductCard from "../components/ProductCard";
import { Button } from "../components/ui/button";
import { ArrowRight, PackageSearch, Loader2 } from "lucide-react";

const CategoryPage = () => {
  const { slug } = useParams();
  const { categories } = useContent();
  const category = categories.find((c) => c.slug === slug);
  const [active, setActive] = useState("All");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setActive("All");
    fetchProducts(slug)
      .then((data) => setItems(data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [slug]);

  const filtered = useMemo(
    () => (active === "All" ? items : items.filter((p) => p.subcategory === active)),
    [active, items]
  );

  if (!category) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-32 text-center">
        <PackageSearch className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
        <h1 className="font-display font-bold text-2xl">Category not found</h1>
        <Link to="/" className="text-[#C9A227] mt-3 inline-block">Back to Home</Link>
      </div>
    );
  }

  return (
    <div>
      <section className="relative h-[42vh] min-h-[320px] overflow-hidden bg-[#14171c]">
        <img src={category.image} alt={category.name} className="w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0e1116]/90 to-[#0e1116]/40" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <nav className="text-sm text-neutral-300 mb-3">
              <Link to="/" className="hover:text-[#C9A227]">Home</Link>
              <span className="mx-2 text-neutral-500">/</span>
              <span className="text-[#E7C766]">{category.name}</span>
            </nav>
            <h1 className="font-display font-extrabold text-4xl md:text-5xl text-white max-w-2xl">{category.name}</h1>
            <p className="text-neutral-300 mt-4 max-w-2xl leading-relaxed">{category.description}</p>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap gap-2.5 mb-10">
            {["All", ...category.subcategories].map((s) => (
              <button key={s} onClick={() => setActive(s)}
                className={`px-5 py-2 rounded-full text-sm font-semibold border transition-colors ${active === s ? "bg-[#C9A227] border-[#C9A227] text-white" : "bg-white border-neutral-200 text-neutral-700 hover:border-[#C9A227] hover:text-[#C9A227]"}`}>
                {s}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="py-24 flex justify-center"><Loader2 className="w-8 h-8 text-[#C9A227] animate-spin" /></div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
              {filtered.map((p) => (<ProductCard key={p.id} product={p} />))}
            </div>
          )}
        </div>
      </section>

      <section className="bg-neutral-50 py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-2xl md:text-3xl text-[#14171c]">Can’t find exactly what you need?</h2>
          <p className="text-neutral-600 mt-3">We are a full custom manufacturer — send us your idea, sketch or logo and we’ll make it.</p>
          <Link to="/contact"><Button className="mt-6 bg-[#C9A227] hover:bg-[#A6841B] text-white rounded-full h-12 px-7">Request a Custom Quote <ArrowRight className="w-4 h-4 ml-1" /></Button></Link>
        </div>
      </section>
    </div>
  );
};

export default CategoryPage;
